import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { isAdminRole } from '../lib/rolePermissions';
import {
  normalizeMonthlyAiBudgetUsd,
  utcMonthKey,
} from '../lib/aiUsageSpend';

/**
 * Live current-month AI spend summary for admin nav / breakdown header.
 * @param {{ companyId?: string, role?: string } | null | undefined} userData
 */
export function useCompanyAiUsageMonth(userData) {
  const companyId =
    typeof userData?.companyId === 'string' ? userData.companyId.trim() : '';
  const role = userData?.role;
  const allowed = Boolean(companyId && isAdminRole(role));

  // Recompute on focus/visibility so a long-lived tab crossing UTC midnight
  // on month boundary picks up the new usageMonthly doc without a full reload.
  const [monthKey, setMonthKey] = useState(() => utcMonthKey());

  useEffect(() => {
    const refreshKey = () => {
      const next = utcMonthKey();
      setMonthKey((prev) => (prev === next ? prev : next));
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshKey();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshKey);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshKey);
    };
  }, []);

  const [spendUsd, setSpendUsd] = useState(0);
  const [callCount, setCallCount] = useState(0);
  const [pricingMissingCount, setPricingMissingCount] = useState(0);
  const [budgetUsd, setBudgetUsd] = useState(null);
  const [loading, setLoading] = useState(allowed);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!allowed) {
      setSpendUsd(0);
      setCallCount(0);
      setPricingMissingCount(0);
      setBudgetUsd(null);
      setLoading(false);
      setError('');
      return undefined;
    }

    setLoading(true);
    setError('');
    let monthlyReady = false;
    let companyReady = false;
    const maybeDone = () => {
      if (monthlyReady && companyReady) setLoading(false);
    };

    const unsubMonth = onSnapshot(
      doc(db, 'companies', companyId, 'usageMonthly', monthKey),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setSpendUsd(
          typeof data.spendUsd === 'number' && Number.isFinite(data.spendUsd)
            ? data.spendUsd
            : 0,
        );
        setCallCount(
          typeof data.callCount === 'number' && Number.isFinite(data.callCount)
            ? data.callCount
            : 0,
        );
        setPricingMissingCount(
          typeof data.pricingMissingCount === 'number' &&
            Number.isFinite(data.pricingMissingCount)
            ? data.pricingMissingCount
            : 0,
        );
        monthlyReady = true;
        maybeDone();
      },
      (err) => {
        console.error('useCompanyAiUsageMonth monthly:', err);
        setError(err?.message || 'Failed to load monthly AI spend.');
        monthlyReady = true;
        maybeDone();
      },
    );

    const unsubCompany = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        const settings =
          data.settings && typeof data.settings === 'object'
            ? data.settings
            : {};
        setBudgetUsd(normalizeMonthlyAiBudgetUsd(settings.monthlyAiBudgetUsd));
        companyReady = true;
        maybeDone();
      },
      (err) => {
        console.error('useCompanyAiUsageMonth company:', err);
        setError(err?.message || 'Failed to load AI budget setting.');
        companyReady = true;
        maybeDone();
      },
    );

    return () => {
      unsubMonth();
      unsubCompany();
    };
  }, [allowed, companyId, monthKey]);

  return {
    allowed,
    monthKey,
    spendUsd,
    callCount,
    pricingMissingCount,
    budgetUsd,
    loading,
    error,
  };
}
